import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import ObsessiveCompulsiveTestForm from "./ObsessiveCompulsiveTestForm";
import { fetchOcdAssessment, updateOcdAssessment } from "../../../store/slices/ocdAssessmentsSlice";

const EditObsessiveCompulsiveTest = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        clientName: "",
        diagnosis: "",
        assessedBy: "",
        obsTime: "",
        obsTimeValue: "",
        obsInterference: "",
        obsInterferenceValue: "",
        obsDistress: "",
        obsDistressValue: "",
        obsResistance: "",
        obsResistanceValue: "",
        obsControl: "",
        obsControlValue: "",
        compTime: "",
        compTimeValue: "",
        compInterference: "",
        compInterferenceValue: "",
        compAnxiety: "",
        compAnxietyValue: "",
        compResistance: "",
        compResistanceValue: "",
        compControl: "",
        compControlValue: ""
    });

    useEffect(() => {
        const loadAssessment = async () => {
            try {
                const result = await dispatch(fetchOcdAssessment(id)).unwrap();
                // Map API data to formData
                setFormData({
                    caseId: result.case_id || "",
                    patientId: result.patient_id || "",
                    clientName: result.client_name || "",
                    diagnosis: result.diagnosis || "",
                    assessedBy: result.assessed_by || "",
                    obsTime: result.obsessions_time_score?.toString() || "",
                    obsTimeValue: result.obsessions_time_value || "",
                    obsInterference: result.obsessions_interference_score?.toString() || "",
                    obsInterferenceValue: result.obsessions_interference_value || "",
                    obsDistress: result.obsessions_distress_score?.toString() || "",
                    obsDistressValue: result.obsessions_distress_value || "",
                    obsResistance: result.obsessions_resistance_score?.toString() || "",
                    obsResistanceValue: result.obsessions_resistance_value || "",
                    obsControl: result.obsessions_control_score?.toString() || "",
                    obsControlValue: result.obsessions_control_value || "",
                    compTime: result.compulsions_time_score?.toString() || "",
                    compTimeValue: result.compulsions_time_value || "",
                    compInterference: result.compulsions_interference_score?.toString() || "",
                    compInterferenceValue: result.compulsions_interference_value || "",
                    compAnxiety: result.compulsions_anxiety_score?.toString() || "",
                    compAnxietyValue: result.compulsions_anxiety_value || "",
                    compResistance: result.compulsions_resistance_score?.toString() || "",
                    compResistanceValue: result.compulsions_resistance_value || "",
                    compControl: result.compulsions_control_score?.toString() || "",
                    compControlValue: result.compulsions_control_value || ""
                });
            } catch (error) {
                Swal.fire("Error", "Failed to fetch assessment data", "error");
            }
        };

        loadAssessment();
    }, [dispatch, id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const mapFormDataToPayload = () => {
        return {
            case_id: formData.caseId,
            patient_id: formData.patientId,
            client_name: formData.clientName,
            diagnosis: formData.diagnosis,
            assessed_by: formData.assessedBy,
            obsessions_time_score: parseInt(formData.obsTime || 0, 10),
            obsessions_time_value: formData.obsTimeValue || "",
            obsessions_interference_score: parseInt(formData.obsInterference || 0, 10),
            obsessions_interference_value: formData.obsInterferenceValue || "",
            obsessions_distress_score: parseInt(formData.obsDistress || 0, 10),
            obsessions_distress_value: formData.obsDistressValue || "",
            obsessions_resistance_score: parseInt(formData.obsResistance || 0, 10),
            obsessions_resistance_value: formData.obsResistanceValue || "",
            obsessions_control_score: parseInt(formData.obsControl || 0, 10),
            obsessions_control_value: formData.obsControlValue || "",
            compulsions_time_score: parseInt(formData.compTime || 0, 10),
            compulsions_time_value: formData.compTimeValue || "",
            compulsions_interference_score: parseInt(formData.compInterference || 0, 10),
            compulsions_interference_value: formData.compInterferenceValue || "",
            compulsions_anxiety_score: parseInt(formData.compAnxiety || 0, 10),
            compulsions_anxiety_value: formData.compAnxietyValue || "",
            compulsions_resistance_score: parseInt(formData.compResistance || 0, 10),
            compulsions_resistance_value: formData.compResistanceValue || "",
            compulsions_control_score: parseInt(formData.compControl || 0, 10),
            compulsions_control_value: formData.compControlValue || "",
            total_score: Object.values(formData)
                .filter((_, idx) => idx >= 5) // scores only
                .reduce((sum, val) => sum + (parseInt(val || 0, 10)), 0),
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await dispatch(updateOcdAssessment({ case_id: caseId, assessmentData: mapFormDataToPayload() })).unwrap();
            Swal.fire("Updated!", "Obsessive Compulsive Test updated successfully.", "success");
            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } catch (err) {
            Swal.fire("Error", "Failed to update assessment", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase04/obsessive-compulsive-test/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <ObsessiveCompulsiveTestForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i>
                                        Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditObsessiveCompulsiveTest;
